import type { PropertyName } from "@frui.ts/helpers/dist";
import { bound } from "@frui.ts/helpers/dist";
import { watchBusy } from "@frui.ts/screens";
import { attachAutomaticValidator, validateAll } from "@frui.ts/validation/dist";
import * as Sentry from "@sentry/react";
import type { EntityNames } from "config";
import { Modules } from "config";
import BankAccountBlacklist, { BankAccountBlacklistValidationRules } from "entities/bankAccountBlacklist";
import { computed, reaction } from "mobx";
import type BankAccountBlacklistRepository from "repositories/bankAccountBlacklistRepository";
import type EventBus from "services/eventBus";
import { blacklistedAccountsChanged } from "services/eventBus";
import type LocalizationService from "services/localizationService";
import type NotificationService from "services/notificationService";
import type SecurityService from "services/securityService";
import { BankAccountFormatType } from "entities/bankAccountFormatType";
import type OptionsService from "services/optionsService";
import type RefundDisapprovalService from "../../../services/refundDisapprovalService";
import { serializeAccountNumber } from "../../../utils/filters";
import type AlertsViewModel from "../../alertsViewModel";
import CreateViewModel from "../../createViewModel";
import type BankAccount from "../../../entities/bankAccount";
import { BankAccountValidationRules } from "../../../entities/bankAccount";
import { detectAccountType, setAccountNumberAndCodeByType } from "@edaz/utils/bankAccount";

export default class BankAccountBlacklistCreateViewModel extends CreateViewModel<BankAccountBlacklist> {
  readonly modelName = "bankAccountBlacklist";
  private unsubscriptions: Array<() => void> = [];

  @computed
  get accountFormatTypes() {
    return this.optionsService.internalEnumOptions(BankAccountFormatType);
  }

  constructor(
    protected repository: BankAccountBlacklistRepository,
    protected localizationService: LocalizationService,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    private optionsService: OptionsService,
    private refundsDisapprovalService: RefundDisapprovalService,
    public alertsViewModel: AlertsViewModel
  ) {
    super();
  }

  @bound
  @watchBusy
  async create() {
    if (validateAll([this.item, this.item.bankAccount])) {
      this.item.bankAccount.accountNumber = serializeAccountNumber(this.item.bankAccount.accountNumber) ?? "";
      try {
        const confirmed = await this.refundsDisapprovalService.disapproveWithConfirmation(this.item);

        if (confirmed) {
          await super.create();
          this.eventBus.publish(
            blacklistedAccountsChanged({
              bankAccount: { accountNumber: this.item.bankAccount.accountNumber, bank: this.item.bankAccount.bank },
            })
          );
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  protected loadDetail() {
    const item = new BankAccountBlacklist();
    attachAutomaticValidator(item, BankAccountBlacklistValidationRules);
    attachAutomaticValidator(item.bankAccount, BankAccountValidationRules);
    this.unsubscriptions.push(reaction(() => item.bankAccount._formatType, this.onFormatTypeChanged));

    return item;
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @bound
  private onFormatTypeChanged() {
    this.item.bankAccount.accountNumber = "";
    this.item.bankAccount.bank = "";
  }

  @bound
  onHide() {
    this.requestClose();
  }

  @bound
  onInvalidAccountNumberPasted(value: string, event: ClipboardEvent) {
    const accountType = detectAccountType(value);
    this.item.bankAccount._formatType = accountType;
    setAccountNumberAndCodeByType(value, accountType, this.item.bankAccount);
    // We need to prevent default beacuse we're setting the input value above
    event.preventDefault();
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `bankAccountBlacklists.${code}`, size);
  }

  translateSubAttribute(entityName: "bankAccount", attribute: PropertyName<BankAccount>): string;
  translateSubAttribute(entityName: EntityNames, attribute: string) {
    return this.localizationService.translateAttribute(entityName, attribute);
  }
}
